import React, { useEffect, useMemo, useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';

import { creerDeploiement } from './deploiementApi';
import SyncCamDeploiementResume from './SyncCamDeploiementResume';

const etapes = ['Qualification', 'Gates SyncCam', 'Decision', 'Observation', 'Resume'];
const CLE_BROUILLON = 'deploiement_synccam_brouillon_v1';

const gatesReference = [
  {
    id: 'capture_raw_reelle',
    titre: 'Capture RAW reelle',
    objectif: 'Confirmer qu une camera pilote produit un RAW exploitable, issu d une captation reelle.',
    procedure: 'Lancer une captation pilote, laisser finaliser un segment, puis verifier la presence du RAW et du jalon de finalisation.',
    preuvesAttendues: 'Chemin RAW, appareil/camera, horodatage, extrait SyncCamLog avec CAMERA_FINALIZE_OK ou preuve equivalente.',
    automatisationFuture: 'Detection automatique RAW + CAMERA_FINALIZE_OK via Cockpit Monitoring et projection operationnelle.',
  },
  {
    id: 'evenement_decoupe_reprise',
    titre: 'Evenement -> decoupe -> reprise camera',
    objectif: 'Verifier qu un evenement declenche la decoupe attendue sans laisser la camera arretee.',
    procedure: 'Produire un evenement pilote, observer le debut de decoupe, puis confirmer la reprise de capture.',
    preuvesAttendues: 'Evenement source, segment decoupe, sequence runtime montrant reprise ou absence de CAMERA_RECORDING_LOST.',
    automatisationFuture: 'Correlation evenement, CUT_START/CUT_END, reprise camera et absence de perte dans le graphe vivant.',
  },
  {
    id: 'decoupe_upload_disponibilite',
    titre: 'Decoupe -> upload -> disponibilite en ligne',
    objectif: 'Confirmer que le segment decoupe quitte le device et devient consultable en ligne.',
    procedure: 'Suivre un segment depuis la decoupe jusqu a l upload, puis verifier sa disponibilite cote serveur.',
    preuvesAttendues: 'Identifiant segment, traces UPLOAD_* sans echec, URL ou preuve de disponibilite backend.',
    automatisationFuture: 'Rapprochement automatique entre signaux UPLOAD_*, backend video et disponibilite web.',
  },
  {
    id: 'rotation_raw_longue_duree',
    titre: 'Rotation RAW longue duree',
    objectif: 'S assurer que la rotation ne cree pas de trou de captation ni de segment orphelin.',
    procedure: 'Observer une captation longue ou une fenetre pilote suffisante pour couvrir plusieurs rotations RAW.',
    preuvesAttendues: 'Liste des segments, durees, transitions de rotation, absence de trou non justifie.',
    automatisationFuture: 'Calcul de continuite par session camera et alerte sur trou de timeline.',
  },
  {
    id: 'crash_recuperation_automatique',
    titre: 'Crash -> recuperation automatique',
    objectif: 'Verifier que la release gere un crash camera sans intervention manuelle durable.',
    procedure: 'Documenter un crash observe ou provoque, puis verifier CAMERA_RECOVERY_START et resultat de reprise.',
    preuvesAttendues: 'CAMERA_CRASH, recovery, etat final, duree d indisponibilite, segment suivant exploitable.',
    automatisationFuture: 'Boucle crash/recovery reconstruite depuis les signaux runtime enrichis.',
  },
  {
    id: 'monitoring_remonte',
    titre: 'Monitoring remonte',
    objectif: 'Confirmer que les signaux SyncCam arrivent dans Monitoring et restent exploitables.',
    procedure: 'Verifier le dernier fichier recu, les lignes parsees et l affichage dans le cockpit ou Incidents Camera.',
    preuvesAttendues: 'Poller OK, dernier fichier recu, lignes monitoring_log_ligne, capture ou lien cockpit.',
    automatisationFuture: 'Preuve automatique issue du poller, de la sante recente et de la projection par camera.',
  },
  {
    id: 'crash_reporting',
    titre: 'Crash reporting',
    objectif: 'Valider que les crashs et erreurs runtime remontent avec assez de contexte pour enquete.',
    procedure: 'Observer une erreur runtime ou un crash, puis confirmer correlation/session/camera et message exploitable.',
    preuvesAttendues: 'Erreur enrichie, correlationId/sessionId si disponible, cameraState/runtimeState ou payload brut.',
    automatisationFuture: 'Controle automatique du schema minimal de crash reporting dans les incidents camera.',
  },
  {
    id: 'convergence_silencieuse',
    titre: 'Convergence silencieuse',
    objectif: 'Distinguer silence normal, retard d ingestion et camera muette avant promotion.',
    procedure: 'Observer une periode sans incident, puis verifier heartbeat, dernier fichier recu et absence de dette runtime.',
    preuvesAttendues: 'Heartbeat recent, monitoring_camera_silence non critique, aucun backlog upload, commentaires operateur.',
    automatisationFuture: 'Synthese automatique silence/heartbeat/convergence depuis Cockpit Operationnel.',
  },
];

const construireGatesInitiales = () => gatesReference.reduce((acc, gate) => ({
  ...acc,
  [gate.id]: {
    statut: 'a_valider',
    preuves: '',
    observations: '',
    automatisable: false,
  },
}), {});

const etatInitial = {
  operateur: '',
  version: '',
  commit: '',
  appareilsPilotes: '',
  notesQualification: '',
  gates: construireGatesInitiales(),
  decisionFinale: '',
  commentaireDecision: '',
  observationPostDeploiement: {
    incidents: '',
    monitoring: '',
    heartbeat: '',
    convergence: '',
    uploads: '',
    detteRuntime: '',
    commentaires: '',
  },
};

const normaliserEtatBrouillon = (etatSauvegarde = {}) => ({
  ...etatInitial,
  operateur: etatSauvegarde.operateur || '',
  version: etatSauvegarde.version || '',
  commit: etatSauvegarde.commit || '',
  appareilsPilotes: etatSauvegarde.appareilsPilotes || '',
  notesQualification: etatSauvegarde.notesQualification || '',
  gates: { ...construireGatesInitiales(), ...(etatSauvegarde.gates || {}) },
  decisionFinale: etatSauvegarde.decisionFinale || '',
  commentaireDecision: etatSauvegarde.commentaireDecision || '',
  observationPostDeploiement: {
    ...etatInitial.observationPostDeploiement,
    ...(etatSauvegarde.observationPostDeploiement || {}),
  },
});

const normaliserEtapeBrouillon = (etapeActiveSauvegardee, etatSauvegarde = {}) => {
  const index = typeof etapeActiveSauvegardee === 'number' ? etapeActiveSauvegardee : 0;
  const ancienFormat = Boolean(
    etatSauvegarde.verificationsTechniques ||
    Object.prototype.hasOwnProperty.call(etatSauvegarde, 'environnementCible') ||
    Object.prototype.hasOwnProperty.call(etatSauvegarde, 'build') ||
    Object.prototype.hasOwnProperty.call(etatSauvegarde, 'branche') ||
    Object.prototype.hasOwnProperty.call(etatSauvegarde, 'canal')
  );
  const indexCourant = ancienFormat && index > 1 ? index - 1 : index;
  return Math.max(0, Math.min(indexCourant, etapes.length - 1));
};

const statutsAcceptesPourGate = ['ok', 'ok_surveillance', 'non_applicable'];

const contexteValide = (etat) => Boolean(
  etat.operateur.trim() &&
  etat.version.trim() &&
  etat.appareilsPilotes.trim()
);

const gatesValides = (etat) => gatesReference.every((gate) => {
  const valeur = etat.gates[gate.id] || {};
  if (!statutsAcceptesPourGate.includes(valeur.statut)) return false;
  if (valeur.statut !== 'non_applicable' && !valeur.preuves.trim()) return false;
  return true;
});

const decisionValide = (etat) => Boolean(
  etat.decisionFinale &&
  (etat.decisionFinale === 'promouvable' || etat.commentaireDecision.trim())
);

const observationValide = (etat) => {
  if (etat.decisionFinale === 'a_retenir' || etat.decisionFinale === 'bloquant') {
    return true;
  }
  const o = etat.observationPostDeploiement;
  return Boolean(o.monitoring.trim() && o.heartbeat.trim() && o.convergence.trim() && o.uploads.trim());
};

const etapeValide = (etat, index) => {
  if (index === 0) return contexteValide(etat);
  if (index === 1) return gatesValides(etat);
  if (index === 2) return decisionValide(etat);
  if (index === 3) return observationValide(etat);
  if (index === 4) return contexteValide(etat) && gatesValides(etat) && decisionValide(etat) && observationValide(etat);
  return false;
};

const construireRapport = (etat) => ({
  type: 'deploiement_synccam',
  dateRapport: new Date().toISOString(),
  operateur: etat.operateur,
  referenceVersion: {
    commit: etat.commit,
    version: etat.version,
  },
  qualificationRelease: {
    appareilsPilotes: etat.appareilsPilotes,
    notes: etat.notesQualification,
  },
  gatesFonctionnels: gatesReference.map((gate) => ({
    ...gate,
    ...(etat.gates[gate.id] || {}),
  })),
  decisionFinale: etat.decisionFinale,
  commentaireDecision: etat.commentaireDecision,
  observationPostDeploiement: { ...etat.observationPostDeploiement },
  rapport: {
    titre: 'Rapport de promotion SyncCam',
    sections: [
      { nom: 'Qualification de la release', contenu: { operateur: etat.operateur, version: etat.version, commit: etat.commit, appareilsPilotes: etat.appareilsPilotes, notes: etat.notesQualification } },
      { nom: 'Gates fonctionnels SyncCam', contenu: gatesReference.map((gate) => ({ titre: gate.titre, ...(etat.gates[gate.id] || {}) })) },
      { nom: 'Decision de promotion', contenu: { decisionFinale: etat.decisionFinale, commentaireDecision: etat.commentaireDecision } },
      { nom: 'Observation post-deploiement', contenu: { ...etat.observationPostDeploiement } },
    ],
  },
});

function SyncCamDeploiementSkeleton({ onCreation }) {
  const [etat, setEtat] = useState(etatInitial);
  const [etapeActive, setEtapeActive] = useState(0);
  const [gateActive, setGateActive] = useState(gatesReference[0].id);
  const [soumissionEnCours, setSoumissionEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [pretSauvegarde, setPretSauvegarde] = useState(false);
  const [derniereSauvegarde, setDerniereSauvegarde] = useState('');

  const rapport = useMemo(() => construireRapport(etat), [etat]);

  const changerChamp = (nom, valeur) => setEtat((precedent) => ({ ...precedent, [nom]: valeur }));
  const changerObservation = (nom, valeur) => setEtat((precedent) => ({ ...precedent, observationPostDeploiement: { ...precedent.observationPostDeploiement, [nom]: valeur } }));
  const changerGate = (id, nom, valeur) => setEtat((precedent) => ({ ...precedent, gates: { ...precedent.gates, [id]: { ...precedent.gates[id], [nom]: valeur } } }));

  useEffect(() => {
    try {
      const brut = localStorage.getItem(CLE_BROUILLON);
      if (brut) {
        const sauvegarde = JSON.parse(brut);
        if (sauvegarde && sauvegarde.etat) {
          setEtat(normaliserEtatBrouillon(sauvegarde.etat));
          setEtapeActive(normaliserEtapeBrouillon(sauvegarde.etapeActive, sauvegarde.etat));
          setGateActive(sauvegarde.gateActive || gatesReference[0].id);
          setDerniereSauvegarde(sauvegarde.derniereSauvegarde || '');
          setSucces('Un brouillon SyncCam a ete restaure automatiquement.');
        }
      }
    } catch (e) {
      setErreur('Impossible de restaurer le brouillon SyncCam.');
    } finally {
      setPretSauvegarde(true);
    }
  }, []);

  useEffect(() => {
    if (!pretSauvegarde) return;
    try {
      const initial = JSON.stringify(etat) === JSON.stringify(etatInitial) && etapeActive === 0;
      if (initial) {
        localStorage.removeItem(CLE_BROUILLON);
        setDerniereSauvegarde('');
        return;
      }
      const horodatage = new Date().toISOString();
      localStorage.setItem(CLE_BROUILLON, JSON.stringify({ etat, etapeActive, gateActive, derniereSauvegarde: horodatage }));
      setDerniereSauvegarde(horodatage);
    } catch (e) {
      setErreur('Impossible de sauvegarder le brouillon SyncCam.');
    }
  }, [etat, etapeActive, gateActive, pretSauvegarde]);

  const suivant = () => {
    if (!etapeValide(etat, etapeActive)) {
      setErreur('Des informations critiques sont manquantes sur cette etape.');
      return;
    }
    setEtapeActive((precedent) => Math.min(precedent + 1, etapes.length - 1));
  };

  const precedent = () => setEtapeActive((precedent) => Math.max(precedent - 1, 0));

  const annuler = () => {
    const ok = window.confirm('Voulez-vous annuler cette validation SyncCam et supprimer le brouillon local ?');
    if (!ok) return;
    setEtat(etatInitial);
    setEtapeActive(0);
    setGateActive(gatesReference[0].id);
    setDerniereSauvegarde('');
    localStorage.removeItem(CLE_BROUILLON);
    setSucces('La validation SyncCam en cours a ete annulee.');
  };

  const soumettre = async () => {
    if (!etapeValide(etat, 4)) {
      setErreur('Le rapport SyncCam est incomplet. Merci de finaliser les preuves et la decision.');
      return;
    }
    setSoumissionEnCours(true);
    setErreur('');
    try {
      const resultat = await creerDeploiement(rapport);
      setSucces('Rapport SyncCam enregistre avec succes.');
      setEtat(etatInitial);
      setEtapeActive(0);
      setGateActive(gatesReference[0].id);
      setDerniereSauvegarde('');
      localStorage.removeItem(CLE_BROUILLON);
      if (onCreation) onCreation(resultat);
    } catch (err) {
      setErreur(err.message || 'Impossible d enregistrer le rapport SyncCam.');
    } finally {
      setSoumissionEnCours(false);
    }
  };

  const vueQualification = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <TextField fullWidth variant="outlined" label="Operateur" value={etat.operateur} onChange={(e) => changerChamp('operateur', e.target.value)} />
      </Grid>
      <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" label="Version" value={etat.version} onChange={(e) => changerChamp('version', e.target.value)} /></Grid>
      <Grid item xs={12} md={4}><TextField fullWidth variant="outlined" label="Commit" value={etat.commit} onChange={(e) => changerChamp('commit', e.target.value)} helperText="Optionnel si non disponible" /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Appareils pilotes" value={etat.appareilsPilotes} onChange={(e) => changerChamp('appareilsPilotes', e.target.value)} helperText="Exemple: TEL-1001 / CAM-77, TEL-1002" /></Grid>
      <Grid item xs={12}><TextField fullWidth multiline rows={3} variant="outlined" label="Notes de qualification" value={etat.notesQualification} onChange={(e) => changerChamp('notesQualification', e.target.value)} /></Grid>
    </Grid>
  );

  const gateSelectionnee = gatesReference.find((gate) => gate.id === gateActive) || gatesReference[0];
  const etatGate = etat.gates[gateSelectionnee.id] || {};

  const vueGates = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <Paper style={{ padding: 12, textAlign: 'left' }}>
          <Typography variant="subtitle2" style={{ marginBottom: 10 }}>Gates de promotion</Typography>
          {gatesReference.map((gate, index) => {
            const courant = etat.gates[gate.id] || {};
            const complet = statutsAcceptesPourGate.includes(courant.statut) && (courant.statut === 'non_applicable' || Boolean((courant.preuves || '').trim()));
            return (
              <Button
                key={gate.id}
                fullWidth
                variant={gateActive === gate.id ? 'contained' : 'outlined'}
                color={complet ? 'primary' : 'default'}
                onClick={() => setGateActive(gate.id)}
                style={{ marginBottom: 8, justifyContent: 'flex-start', textAlign: 'left' }}
              >
                {(index + 1) + '. ' + gate.titre}
              </Button>
            );
          })}
        </Paper>
      </Grid>
      <Grid item xs={12} md={8}>
        <Paper style={{ padding: 14, textAlign: 'left' }}>
          <Typography variant="h6">{gateSelectionnee.titre}</Typography>
          <Typography variant="body2" style={{ marginTop: 10 }}><strong>Objectif:</strong> {gateSelectionnee.objectif}</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}><strong>Procedure:</strong> {gateSelectionnee.procedure}</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}><strong>Preuves attendues:</strong> {gateSelectionnee.preuvesAttendues}</Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}><strong>Automatisation future:</strong> {gateSelectionnee.automatisationFuture}</Typography>
          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={5}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel id={'statut-' + gateSelectionnee.id}>Statut</InputLabel>
                <Select labelId={'statut-' + gateSelectionnee.id} label="Statut" value={etatGate.statut || 'a_valider'} onChange={(e) => changerGate(gateSelectionnee.id, 'statut', e.target.value)}>
                  <MenuItem value="a_valider">A valider</MenuItem>
                  <MenuItem value="ok">OK</MenuItem>
                  <MenuItem value="ok_surveillance">OK avec surveillance</MenuItem>
                  <MenuItem value="ko">KO</MenuItem>
                  <MenuItem value="non_applicable">Non applicable</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={7}>
              <FormControlLabel control={<Checkbox color="primary" checked={Boolean(etatGate.automatisable)} onChange={(e) => changerGate(gateSelectionnee.id, 'automatisable', e.target.checked)} />} label="Preuve candidate a une automatisation future" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} variant="outlined" label="Preuves collectees" value={etatGate.preuves || ''} onChange={(e) => changerGate(gateSelectionnee.id, 'preuves', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} variant="outlined" label="Observations" value={etatGate.observations || ''} onChange={(e) => changerGate(gateSelectionnee.id, 'observations', e.target.value)} />
            </Grid>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  const vueDecision = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={4}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="synccam-decision-label">Decision de promotion</InputLabel>
          <Select labelId="synccam-decision-label" label="Decision de promotion" value={etat.decisionFinale} onChange={(e) => changerChamp('decisionFinale', e.target.value)}>
            <MenuItem value="promouvable">Promouvable</MenuItem>
            <MenuItem value="promouvable_surveillance">Promouvable avec surveillance</MenuItem>
            <MenuItem value="a_retenir">A retenir</MenuItem>
            <MenuItem value="bloquant">Bloquant</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} md={8}>
        <TextField fullWidth multiline rows={3} variant="outlined" label="Justification de decision" value={etat.commentaireDecision} onChange={(e) => changerChamp('commentaireDecision', e.target.value)} />
      </Grid>
    </Grid>
  );

  const vueObservation = (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}><TextField fullWidth multiline rows={3} variant="outlined" label="Incidents observes" value={etat.observationPostDeploiement.incidents} onChange={(e) => changerObservation('incidents', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth multiline rows={3} variant="outlined" label="Monitoring" value={etat.observationPostDeploiement.monitoring} onChange={(e) => changerObservation('monitoring', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Heartbeat" value={etat.observationPostDeploiement.heartbeat} onChange={(e) => changerObservation('heartbeat', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Convergence" value={etat.observationPostDeploiement.convergence} onChange={(e) => changerObservation('convergence', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Uploads" value={etat.observationPostDeploiement.uploads} onChange={(e) => changerObservation('uploads', e.target.value)} /></Grid>
      <Grid item xs={12} md={6}><TextField fullWidth variant="outlined" label="Dette runtime" value={etat.observationPostDeploiement.detteRuntime} onChange={(e) => changerObservation('detteRuntime', e.target.value)} /></Grid>
      <Grid item xs={12}><TextField fullWidth multiline rows={3} variant="outlined" label="Commentaires post-deploiement" value={etat.observationPostDeploiement.commentaires} onChange={(e) => changerObservation('commentaires', e.target.value)} /></Grid>
    </Grid>
  );

  let contenu = vueQualification;
  if (etapeActive === 1) contenu = vueGates;
  if (etapeActive === 2) contenu = vueDecision;
  if (etapeActive === 3) contenu = vueObservation;
  if (etapeActive === 4) contenu = <SyncCamDeploiementResume rapport={rapport} />;

  return (
    <Paper style={{ padding: 16 }}>
      <Typography variant="h5">Validation de promotion SyncCam</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        Assistant guide pour qualifier une release, centraliser les preuves SyncCam et formaliser la decision de promotion.
      </Typography>
      <Typography variant="caption" color="textSecondary" style={{ marginTop: 6, display: 'block' }}>
        {derniereSauvegarde ? ('Brouillon sauvegarde automatiquement: ' + new Date(derniereSauvegarde).toLocaleString()) : 'Brouillon local non initialise.'}
      </Typography>

      <Stepper activeStep={etapeActive} alternativeLabel style={{ marginTop: 16, marginBottom: 16 }}>
        {etapes.map((etape) => <Step key={etape}><StepLabel>{etape}</StepLabel></Step>)}
      </Stepper>

      {contenu}

      <Grid container spacing={2} style={{ marginTop: 12 }}>
        <Grid item><Button variant="outlined" disabled={etapeActive === 0 || soumissionEnCours} onClick={precedent}>Precedent</Button></Grid>
        <Grid item>
          {etapeActive < etapes.length - 1
            ? <Button variant="contained" color="primary" disabled={soumissionEnCours} onClick={suivant}>Suivant</Button>
            : <Button variant="contained" color="primary" disabled={soumissionEnCours} onClick={soumettre}>{soumissionEnCours ? 'Enregistrement...' : 'Soumettre le rapport'}</Button>}
        </Grid>
        <Grid item><Button variant="text" color="secondary" disabled={soumissionEnCours} onClick={annuler}>Annuler la validation en cours</Button></Grid>
      </Grid>

      <Snackbar open={Boolean(erreur)} autoHideDuration={5000} onClose={() => setErreur('')} message={erreur} />
      <Snackbar open={Boolean(succes)} autoHideDuration={4000} onClose={() => setSucces('')} message={succes} />
    </Paper>
  );
}

export default SyncCamDeploiementSkeleton;



// WEBPACK FOOTER //
// src/components/deploiements/SyncCamDeploiementSkeleton.js